/**
 * O que a edição tem fora do papel (E202.F2, GUS-145, ADR-0145 §6; ADR-0136 §1), no fim do documento
 * da mesa, discreto:
 * - as edições de uma versão anterior da análise: "Reaplicar" na que ainda vale; a do texto que mudou
 *   fica com o texto antigo à vista, para reescrever no trecho atual;
 * - as edições que o texto atual não tem mais (o original delas mudou na mesma análise).
 */
import { EdicoesDesatualizadas } from '@/components/CorrecaoDoRascunho/EdicoesDesatualizadas';
import { ptBR } from '@/i18n/pt-BR';
import type { EdicaoDoLaudoData } from '@/lib/correcaoDoRascunho';
import type { DocumentoDaSaidaData } from '@/lib/documentoDaSaida';
import { planoDoMarkup } from '@/lib/markupDaEdicao';

import { ID_DAS_EDICOES_FORA } from './AvisoDaAnaliseNova';
import type { EdicaoNoDocumento } from './useEdicaoNoDocumento';

const tm = ptBR.editorManual;
const tc = ptBR.correcaoDoRascunho;

function NaoAplicadas({ edicoes }: { edicoes: EdicaoDoLaudoData[] }) {
  if (edicoes.length === 0) return null;
  return (
    <section aria-labelledby="nao-aplicadas" data-testid="nao-aplicadas">
      <h3 id="nao-aplicadas" className="text-body-md font-semibold text-fg">
        {tm.naoAplicadas.titulo}
      </h3>
      <p className="max-w-prose text-caption text-fg-muted">{tm.naoAplicadas.ajuda}</p>
      <ul className="mt-sc-2 space-y-sc-2">
        {edicoes.map((e) => (
          <li key={e.id} className="border-t border-border pt-sc-2 text-body-sm">
            {e.texto !== null ? (
              <p className="whitespace-pre-line text-fg">{planoDoMarkup(e.texto)}</p>
            ) : null}
            <p className="text-caption text-fg-muted">
              {tc.desatualizadas.fundamento.replace('{fundamento}', e.fundamento)}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function EdicoesForaDoDocumento({
  documento,
  edicao,
}: {
  documento: DocumentoDaSaidaData;
  edicao: EdicaoNoDocumento | null;
}): JSX.Element | null {
  const { desatualizadas, nao_aplicadas: naoAplicadas } = documento;
  if (desatualizadas.length === 0 && naoAplicadas.length === 0) return null;
  return (
    <aside
      id={ID_DAS_EDICOES_FORA}
      className="mx-auto mt-sc-4 max-w-[56rem] scroll-mt-sc-6 space-y-sc-4 border border-border bg-surface-2 p-sc-4"
      data-testid="edicoes-fora-do-documento"
    >
      <EdicoesDesatualizadas
        desatualizadas={desatualizadas}
        trechos={documento.trechos}
        bloqueado={documento.assinado || edicao === null}
        salvando={edicao?.salvando ?? false}
        onReaplicar={(id) => edicao?.reaplicar(id)}
      />
      <NaoAplicadas edicoes={naoAplicadas} />
    </aside>
  );
}
